import { CubeContext, useCubeMeta } from '@cubejs-client/react'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { hasInvalidCubePayload } from '../../../../../Utils/UtilFunctions/cubeUtil'
import fetchAndTrackCubeQuery from '../../../../../Utils/UtilFunctions/trackedCubeQuery'

const useDisconnectedSensors = ({ plantId }: any) => {
	const sensorsList = useSelector((state: any) => state.sensorsList)
	const levelsList = useSelector((state: any) => state.levelsList)
	const metaCubeNames = useCubeMeta()
	const dispatch = useDispatch()
	const { cubejsApi } = useContext(CubeContext)

	const [disconnectedSensorsList, setDisconnectedSensorsList] = useState<any[]>(
		[]
	)
	const [connectionStatusData, setConnectionStatusData] = useState<any>([])

	const usecaseList = useSelector((state: any) => state.usecaseList)
	const plantsList = useSelector((state: any) => state.plantsList)
	const devicesList= useSelector((state:any) => state.devicesList)

	const currentUsecase = useMemo<any>(
		() =>
			usecaseList.usecases.find((usecase: any) => {
				return usecase.routes_use_code === 'motors-and-pumps'
			}),
		[usecaseList?.usecases]
	)

	const currentPlant = useMemo<any>(
		() => plantsList?.plants?.find((plant: any) => plant._id === plantId),
		[plantsList?.plants, plantId]
	)

	const lastValueAggCube = useMemo<any>(
		() =>
			metaCubeNames?.response?.cubes.find(
				(cube: any) =>
					cube.name.includes('LastValueAgg') &&
					!cube.name.includes('Actionable')
			),
		[metaCubeNames?.response?.cubes]
	)

	const currentPlantAssets = useMemo(
		() =>
			levelsList.levels.filter(
				(ele: any) => ele.plant_id === currentPlant?._id
			),
		[levelsList?.levels, currentPlant?._id]
	)

	const mpDevicesLinkedAssets = useMemo(
		() =>
			currentPlantAssets.filter(
				(ele: any) =>
					ele.plant_id === currentPlant?._id &&
					ele.use_case.includes(currentUsecase?._id) &&
					ele.devices.length > 0 &&
					ele.devices.some((item: any) => item.usecase === currentUsecase?._id)
			),
		[currentPlantAssets, currentPlant?._id, currentUsecase?._id]
	)

	const getSensorById = useCallback(
		(sensorId: string) =>
			sensorsList.sensors.find((sensor: any) => sensor._id === sensorId),
		[sensorsList?.sensors]
	)

	const getAsset = useCallback((id: any) =>  {
		return levelsList.levels.find((ele: any) => {
			return ele._id === id
		})
	}, [levelsList?.levels])

	const getDisconnectedSensorsList = useCallback(() => {
		if (!mpDevicesLinkedAssets.length) return
		if (!connectionStatusData.length) return

		const disconnectedSensorsList = mpDevicesLinkedAssets
			.map((asset: any) => {
				const connectedSensorsTagsDeviceName = connectionStatusData.map((ele: any) => [
						ele[`${lastValueAggCube.name}.tagName`],
						ele[`${lastValueAggCube.name}.deviceName`],
					]
				);

				return asset.devices
					.filter((dev: any) => {
						return dev.usecase === currentUsecase._id
					})
					.map((dev: any) => {
						const sensor = getSensorById(dev.sensor_id)
						const fullTag = `${dev.tag}_${sensor.sensor_code}`
						const deviceCode = devicesList?.devices.find((item:any)=> item?._id === dev?.device_id)?.device_code ?? null 
						const isDisconnectedDevice = Boolean(
							connectedSensorsTagsDeviceName.find( 
								([tag, tagDeviceName]: any) => tag.includes(fullTag) && tagDeviceName === deviceCode 
							)
						)

						if (!isDisconnectedDevice) return null

						const assetGroup = getAsset(asset.parent_asset)
						const asset_area = getAsset(assetGroup.parent_asset)

						return {
							asset_group: assetGroup.asset_alias,
							asset_area: asset_area.asset_alias,
							sensors: sensor.sensor_code,
							asset_name: asset.asset_alias,
							sensor_code: dev.sensor_number,
							electrical_number: asset.electrical_number
						}
					})
			})
			.flat(1)
			.filter(Boolean)

		setDisconnectedSensorsList(disconnectedSensorsList)
	}, [mpDevicesLinkedAssets, connectionStatusData, currentUsecase?._id, getAsset, getSensorById, lastValueAggCube])

	//function to get connection status data for all assets
	const getCSData = useCallback(async () => {
		if (!lastValueAggCube) return

		const connection_query: any = {
			dimensions: [
				`${lastValueAggCube.name}.tagName`,
				`${lastValueAggCube.name}.description`,
				`${lastValueAggCube.name}.deviceName`
			],
			measures: [`${lastValueAggCube.name}.lastValue`],
			filters: [
				{
					values: ['_ConnectionStatusOfRadio'],
					member: `${lastValueAggCube.name}.tagName`,
					operator: 'contains'
				},
				{
					values: ['0'],
					member: `${lastValueAggCube.name}.lastValue`,
					operator: 'equals'
				}
			]
		}

		if (hasInvalidCubePayload(connection_query)) {
			return dispatch({
				type: 'LOAD_SNACKBAR',
				payload: {
					isShow: true,
					message: 'Cube query has invalid payload',
					type: 'error',
					open: true
				}
			})
		}

		const { isError, data: CSData } = await fetchAndTrackCubeQuery(
			cubejsApi,
			connection_query
		)

		if (isError) {
			return dispatch({
				type: 'LOAD_SNACKBAR',
				payload: {
					isShow: true,
					message: 'Cube call failed',
					type: 'error',
					open: true
				}
			})
		}

		setConnectionStatusData(CSData[0])
	}, [dispatch, lastValueAggCube, cubejsApi])

	useEffect(() => {
		getDisconnectedSensorsList()
	}, [getDisconnectedSensorsList])

	useEffect(() => {
		getCSData()
	}, [getCSData])

	return {
		disconnectedSensorsList,
		setDisconnectedSensorsList
	};
}

export default useDisconnectedSensors;